import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Birdimg } from 'utils/assets/images';

const ASSIGNMENT_PRINT_FONT_SIZE_PX = 12;
const ASSIGNMENT_PRINT_LINE_HEIGHT = 1.4;
const ASSIGNMENT_PRINT_LINE_HEIGHT_DOM_PX =
    ASSIGNMENT_PRINT_FONT_SIZE_PX * ASSIGNMENT_PRINT_LINE_HEIGHT;
const PDF_SLICE_SAFETY_MARGIN_PX = 4;
const PDF_MIN_PAGE_FILL_RATIO = 0.15;
const ASSIGNMENT_PRINT_BLOCK_SELECTOR =
    '.assignment-print-page-header, .print-break-unit';

const computeAssignmentPageSlices = (
    totalCanvasHeight,
    pageHeightCanvasPx,
    blocks,
    lineHeightCanvasPx
) => {
    const slices = [];
    let currentTop = 0;

    while (currentTop < totalCanvasHeight) {
        let proposedEnd = Math.min(
            currentTop + pageHeightCanvasPx,
            totalCanvasHeight
        );

        if (proposedEnd < totalCanvasHeight && blocks.length > 0) {
            const conflicting = blocks.find(
                (block) =>
                    block.top > currentTop + PDF_SLICE_SAFETY_MARGIN_PX &&
                    block.top < proposedEnd &&
                    block.bottom > proposedEnd
            );

            if (conflicting) {
                const breakAt = Math.floor(conflicting.top - PDF_SLICE_SAFETY_MARGIN_PX);
                const minViableEnd =
                    currentTop + pageHeightCanvasPx * PDF_MIN_PAGE_FILL_RATIO;
                if (breakAt > minViableEnd) {
                    proposedEnd = breakAt;
                }
            }

            const fullPageEnd = Math.min(
                currentTop + pageHeightCanvasPx,
                totalCanvasHeight
            );
            const spanningBlock = blocks.find(
                (block) =>
                    block.top <= currentTop + PDF_SLICE_SAFETY_MARGIN_PX &&
                    block.bottom > proposedEnd &&
                    block.bottom - block.top > pageHeightCanvasPx
            );

            if (spanningBlock && proposedEnd === fullPageEnd && lineHeightCanvasPx > 0) {
                const remaining = proposedEnd - currentTop;
                const snapped =
                    Math.floor(remaining / lineHeightCanvasPx) * lineHeightCanvasPx;
                if (snapped >= lineHeightCanvasPx) {
                    proposedEnd = currentTop + snapped;
                }
            }
        }

        if (proposedEnd <= currentTop) {
            proposedEnd = Math.min(
                currentTop + pageHeightCanvasPx,
                totalCanvasHeight
            );
        }

        slices.push({ startPx: currentTop, endPx: proposedEnd });
        currentTop = proposedEnd;
    }

    return slices;
};

const getAssignmentPrintBlockBounds = (rootElement) => {
    if (!rootElement) {
        return [];
    }

    const containerTop = rootElement.getBoundingClientRect().top;
    return Array.from(rootElement.querySelectorAll(ASSIGNMENT_PRINT_BLOCK_SELECTOR))
        .map((node) => {
            const rect = node.getBoundingClientRect();
            return {
                top: rect.top - containerTop,
                bottom: rect.bottom - containerTop,
            };
        })
        .filter((bounds) => bounds.bottom > bounds.top)
        .sort((a, b) => a.top - b.top);
};

const preloadImage = (src) =>
    new Promise((resolve) => {
        if (!src) {
            resolve(false);
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(true);
        img.onerror = () => resolve(false);
        img.src = src;
    });

const applyAssignmentPrintCloneStyles = (clonedDoc) => {
    if (!clonedDoc) {
        return;
    }
    clonedDoc.querySelectorAll('.assignment-print-banner').forEach((node) => {
        node.style.setProperty('display', 'block', 'important');
        node.style.setProperty('width', '100%', 'important');
        node.style.setProperty('background-color', '#2d8bba', 'important');
        node.style.setProperty('color', '#ffffff', 'important');
    });
    clonedDoc.querySelectorAll('.assignment-print-banner-title').forEach((node) => {
        node.style.setProperty('color', '#ffffff', 'important');
        node.style.setProperty('font-size', '20px', 'important');
    });
    clonedDoc.querySelectorAll(
        '.assignment-print-banner-meta, .assignment-print-banner-meta-label, .assignment-print-banner-meta-value'
    ).forEach((node) => {
        node.style.setProperty('color', '#ffffff', 'important');
    });
    clonedDoc.querySelectorAll('.assignment-print-banner-meta-grid').forEach((node) => {
        node.style.setProperty('display', 'grid', 'important');
        node.style.setProperty('grid-template-columns', 'auto minmax(0, 1fr)', 'important');
        node.style.setProperty('font-size', '12px', 'important');
        node.style.setProperty('line-height', '16px', 'important');
        node.style.setProperty('color', '#ffffff', 'important');
    });
    clonedDoc.querySelectorAll('.assignment-print-banner-meta-label').forEach((node) => {
        node.style.setProperty('color', '#ffffff', 'important');
        node.style.setProperty('text-align', 'right', 'important');
        node.style.setProperty('font-weight', '700', 'important');
    });
    clonedDoc.querySelectorAll('.assignment-print-banner-meta-value').forEach((node) => {
        node.style.setProperty('color', '#ffffff', 'important');
        node.style.setProperty('text-align', 'left', 'important');
    });
    clonedDoc.querySelectorAll('.assignment-print-banner-bird').forEach((node) => {
        node.crossOrigin = 'anonymous';
        node.style.setProperty('display', 'block', 'important');
        node.style.setProperty('height', '100px', 'important');
        node.style.setProperty('width', 'auto', 'important');
    });
};

const buildAssignmentPdfFileName = ({ studentName, assignmentDisplayName, submissionNumber } = {}) => {
    const submissionLabel =
        submissionNumber !== undefined && submissionNumber !== null && submissionNumber !== ''
            ? `Submission-${submissionNumber}`
            : null;
    const segments = [studentName, assignmentDisplayName, submissionLabel]
        .map((part) => (part == null ? '' : String(part).trim()))
        .filter(Boolean);
    if (segments.length === 0) {
        return `Assignment_Questions_Answers_${new Date().toISOString().split('T')[0]}.pdf`;
    }
    const rawBase = segments.join('_');
    const sanitized = rawBase.replace(/[<>:"/\\|?*\x00-\x1f]/g, '_').replace(/_+/g, '_').trim();
    const safeBase = (sanitized || 'assignment-submission').slice(0, 180);
    return `${safeBase}.pdf`;
};

export const generateAssignmentPDF = async ({
    pdfContentRef,
    showAlert,
    clearGeneratingState,
    setPdfData,
    questions,
    answers,
    studentName,
    assignmentDisplayName,
    className,
    submissionNumber,
    aiGrades,
    aiFeedbacks,
    aiSuggestions,
    overallGrade,
    proficiencyLevel
}) => {
    const questionsToPDF = Array.isArray(questions) ? questions : [];
    const answersToPDF = Array.isArray(answers) ? answers : [];
    
    if (questionsToPDF.length === 0 || answersToPDF.length === 0) {
        showAlert('No questions or answers to download', 'error');
        return;
    }

    setPdfData({
        pdfQuestions: questionsToPDF,
        pdfAnswers: answersToPDF,
        pdfStudentName: studentName || '',
        pdfClassName: className || '',
        pdfActivityTitle: assignmentDisplayName || '',
        pdfAiGrades: Array.isArray(aiGrades) ? aiGrades : [],
        pdfAiFeedbacks: Array.isArray(aiFeedbacks) ? aiFeedbacks : [],
        pdfAiSuggestions: Array.isArray(aiSuggestions) ? aiSuggestions : [],
        pdfOverallGrade: overallGrade || '',
        pdfProficiencyLevel: proficiencyLevel || '',
    });

    try {
        await new Promise(resolve => setTimeout(resolve, 500));

        if (!pdfContentRef.current) {
            console.error('[PDF] Content ref not available');
            showAlert('Failed to generate PDF. Please try again.', 'error');
            clearGeneratingState?.();
            return;
        }

        const waitForTranscripts = async (element, maxWaitTime = 5000) => {
            const startTime = Date.now();
            while (Date.now() - startTime < maxWaitTime) {
                if (element.getAttribute('data-transcripts-loaded') === 'true') {
                    return true;
                }
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            return false;
        };

        const waitForImages = async (element, maxWaitTime = 8000) => {
            const startTime = Date.now();
            while (Date.now() - startTime < maxWaitTime) {
                const images = Array.from(element.querySelectorAll('img'));
                if (images.length === 0) {
                    return true;
                }
                const allLoaded = images.every((img) => {
                    if (!img?.src) {
                        return true;
                    }
                    return img.complete && img.naturalWidth > 0;
                });

                if (allLoaded) {
                    return true;
                }
                await new Promise(resolve => setTimeout(resolve, 150));
            }
            return false;
        };

        await waitForTranscripts(pdfContentRef.current);
        await preloadImage(Birdimg);
        await waitForImages(pdfContentRef.current);
        await new Promise(resolve => setTimeout(resolve, 400));

        const element = pdfContentRef.current;
        
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const margin = 8;
        const contentWidth = pdfWidth - (margin * 2);
        
        const contentWidthPx = Math.round((contentWidth / 25.4) * 96);
        
        const originalWidth = element.style.width;
        const originalMaxWidth = element.style.maxWidth;
        const originalDisplay = element.style.display;
        
        element.style.width = `${contentWidthPx}px`;
        element.style.maxWidth = `${contentWidthPx}px`;
        element.style.display = 'block';
        
        await new Promise(resolve => setTimeout(resolve, 400));

        const scrollHeight = element.scrollHeight;
        const blockBoundaries = getAssignmentPrintBlockBounds(element);
        const canvas = await html2canvas(element, {
            useCORS: true,
            logging: false,
            scale: 2,
            backgroundColor: '#ffffff',
            width: contentWidthPx,
            height: scrollHeight,
            windowWidth: contentWidthPx,
            windowHeight: scrollHeight,
            allowTaint: false,
            imageTimeout: 15000,
            onclone: (clonedDoc) => {
                applyAssignmentPrintCloneStyles(clonedDoc);
            },
        });

        element.style.width = originalWidth;
        element.style.maxWidth = originalMaxWidth;
        element.style.display = originalDisplay;

        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const scaleFactor = imgHeight / scrollHeight;
        
        const imgToPdfScale = contentWidth / imgWidth;
        const scaledWidth = contentWidth;
        
        const pageSpace = pdfHeight - (margin * 2);
        const pageHeightCanvasPx = Math.max(1, pageSpace / imgToPdfScale);
        const lineHeightCanvasPx = Math.max(
            1,
            Math.ceil(ASSIGNMENT_PRINT_LINE_HEIGHT_DOM_PX * scaleFactor)
        );

        const scaledBlockBoundaries = blockBoundaries.map((bounds) => ({
            top: Math.max(0, Math.floor(bounds.top * scaleFactor)),
            bottom: Math.min(imgHeight, Math.ceil(bounds.bottom * scaleFactor)),
        }));

        const pageSlices = computeAssignmentPageSlices(
            imgHeight,
            pageHeightCanvasPx,
            scaledBlockBoundaries,
            lineHeightCanvasPx
        );

        const drawCanvasRegion = (sourceY, sourceHeight) => {
            const heightMm = sourceHeight * imgToPdfScale;
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = imgWidth;
            tempCanvas.height = Math.ceil(sourceHeight);
            const tempCtx = tempCanvas.getContext('2d');
            tempCtx.drawImage(
                canvas,
                0, sourceY,
                imgWidth, sourceHeight,
                0, 0,
                imgWidth, sourceHeight
            );
            return { dataUrl: tempCanvas.toDataURL('image/png', 1.0), heightMm };
        };

        let yPosition = margin;

        const ensurePageSpace = (heightMm) => {
            const remaining = pageSpace - (yPosition - margin);
            if (heightMm > remaining && yPosition > margin) {
                pdf.addPage();
                yPosition = margin;
            }
        };

        for (let i = 0; i < pageSlices.length; i += 1) {
            const slice = pageSlices[i];
            const sliceHeightPx = slice.endPx - slice.startPx;
            if (sliceHeightPx <= 0) {
                continue;
            }

            const sliceHeightMm = sliceHeightPx * imgToPdfScale;
            ensurePageSpace(sliceHeightMm);
            const { dataUrl, heightMm } = drawCanvasRegion(slice.startPx, sliceHeightPx);
            pdf.addImage(dataUrl, 'PNG', margin, yPosition, scaledWidth, heightMm);
            yPosition += heightMm;
        }

        const fileName = buildAssignmentPdfFileName({
            studentName,
            assignmentDisplayName,
            submissionNumber
        });
        pdf.save(fileName);
        
        clearGeneratingState?.();
    } catch (error) {
        console.error('[PDF] Error generating PDF:', error);
        showAlert('Failed to generate PDF. Please try again.', 'error');
        clearGeneratingState?.();
    }
};
